import { DatasetSelectField } from '@/components/DatasetSelectField';
import { FileSelectField } from '@/components/FileSelectField';
import { JsonSchemaForm } from '@/components/JsonSchemaForm';
import { useDarkMode } from '@/contexts/DarkModeContext';
import {
  useCreatePipelineExecution,
  usePipelineExecutionOutput,
  usePipelineExecutionProgress,
  usePipelineExecutionsByPipelineId,
} from '@/queries/pipelines';
import { getErrorMessage } from '@/utils';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Empty from 'antd/es/empty';
import Form from 'antd/es/form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { JsonEditor } from 'ui-components';

import { AnimatedProgress } from './AnimatedProgress';
import { ExecutionHistoryModal } from './ExecutionHistoryModal';
import { RecentRunsDropdown } from './RecentRunsDropdown';

interface RunPipelineProps {
  pipelineId: string;
  configVersionId: string;
  inputSchema: Record<string, unknown> | null;
  pipelineConfig: Record<string, unknown> | null;
  isValidating: boolean;
  teamId: string;
  onExecutionStatusChange: (isRunning: boolean) => void;
}

type RightColumnState = 'empty' | 'running' | 'complete';

const TwoColumnLayout = styled.div`
  display: flex;
  flex: 1;
  gap: ${({ theme }) => theme.paddingMD}px;
  min-height: 0;
  padding: ${({ theme }) => theme.paddingMD}px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const EmptyStateTitle = styled.h3`
  color: ${({ theme }) => theme.colorTextHeading};
  font-size: ${({ theme }) => theme.fontSizeHeading3}px;
  font-weight: ${({ theme }) => theme.fontWeightStrong};
  margin-bottom: ${({ theme }) => theme.marginSM}px;
`;

const EmptyStateDescription = styled.p`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: ${({ theme }) => theme.fontSize}px;
  line-height: ${({ theme }) => theme.lineHeight};
  margin-bottom: ${({ theme }) => theme.marginXXL}px;
`;

const LeftColumn = styled.div`
  flex: 0 0 40%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid ${({ theme }) => theme.colorBorder};
  padding-right: ${({ theme }) => theme.paddingMD}px;

  @media (max-width: 768px) {
    flex: none;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colorBorder};
    padding-right: 0;
    padding-bottom: ${({ theme }) => theme.paddingMD}px;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
`;

const EmptyStatePanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  gap: ${({ theme }) => theme.marginLG}px;
  padding: ${({ theme }) => theme.paddingLG}px;
`;

const ProgressPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: ${({ theme }) => theme.marginMD}px;
  padding: ${({ theme }) => theme.paddingMD}px;
`;

const OutputPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: ${({ theme }) => theme.marginMD}px;
`;

const NotificationBanner = styled.div<{ type: 'info' | 'warning' }>`
  background: ${({ theme, type }) =>
    type === 'info' ? theme.colorFillContent : theme.colorFillAlter};
  padding: ${({ theme }) => `${theme.paddingXS}px ${theme.paddingSM}px`};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  margin-bottom: ${({ theme }) => theme.margin}px;
  font-size: ${({ theme }) => theme.fontSize}px;
  color: ${({ theme, type }) =>
    type === 'info' ? theme.colorTextSecondary : theme.colorPrimary};
`;

const ExecutionHeader = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.marginLG}px;
`;

const ExecutionTitle = styled.h3`
  color: ${({ theme }) => theme.colorTextHeading};
  font-size: ${({ theme }) => theme.fontSizeHeading3}px;
  font-weight: ${({ theme }) => theme.fontWeightStrong};
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

const ExecutionMeta = styled.div`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: ${({ theme }) => theme.fontSize}px;
  line-height: ${({ theme }) => theme.lineHeight};

  > div {
    margin-bottom: ${({ theme }) => theme.marginXXS}px;
  }
`;

const OutputPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.paddingSM}px;
  border-bottom: 1px solid ${({ theme }) => theme.colorBorderSecondary};
`;

const OutputHeader = styled.h4`
  color: ${({ theme }) => theme.colorTextHeading};
  font-size: ${({ theme }) => theme.fontSizeLG}px;
  font-weight: ${({ theme }) => theme.fontWeightStrong};
  margin: 0;
`;

const OutputFormSection = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const OutputContainer = styled.div`
  flex: 1;
  min-height: 0;
`;

const ErrorBanner = styled.div`
  color: ${({ theme }) => theme.colorError};
  background: ${({ theme }) => theme.colorErrorBg || '#fff2f0'};
  padding: ${({ theme }) => theme.paddingSM}px;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  border: ${({ theme }) =>
    `${theme.lineWidth}px solid ${theme.colorErrorBorder || '#ffccc7'}`};
  margin-top: ${({ theme }) => theme.margin}px;
`;

const InputPanel = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginMD}px;
`;

const InputPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.paddingSM}px;
  border-bottom: 1px solid ${({ theme }) => theme.colorBorderSecondary};
`;

const InputFormSection = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const InputTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizeLG}px;
  font-weight: ${({ theme }) => theme.fontWeightStrong};
  color: ${({ theme }) => theme.colorText};
`;

const InputForm = styled(Form)`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;

  .ant-form-item {
    margin-bottom: ${({ theme }) => theme.marginMD}px;
  }

  .ant-form-item:last-child {
    margin-bottom: 0;
  }

  .ant-form-item-label {
    font-weight: ${({ theme }) => theme.fontWeightStrong};
  }

  .field-description {
    color: ${({ theme }) => theme.colorTextSecondary};
    font-size: ${({ theme }) => theme.fontSizeSM}px;
    margin-top: ${({ theme }) => theme.marginXXS}px;
  }
`;

export const RunPipeline: React.FC<RunPipelineProps> = ({
  pipelineId,
  configVersionId,
  inputSchema,
  pipelineConfig,
  isValidating,
  teamId,
  onExecutionStatusChange,
}) => {
  const { message } = App.useApp();
  const [isDarkMode] = useDarkMode();
  const [inputForm] = Form.useForm();

  // Right column state management - derive from execution state
  const [manualRightColumnState, setManualRightColumnState] =
    useState<RightColumnState>('empty');

  const [executionId, setExecutionId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [s3Content, setS3Content] = useState<string | null>(null);
  const [isLoadingS3Content, setIsLoadingS3Content] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [viewingHistoricalExecution, setViewingHistoricalExecution] =
    useState(false);

  const createPipelineExecutionMutation = useCreatePipelineExecution();

  // Track execution progress
  const executionProgress = usePipelineExecutionProgress(executionId);
  const { execution, isRunning, isComplete, progressData } = executionProgress;

  // Notify parent component when execution status changes
  useEffect(() => {
    onExecutionStatusChange(isRunning);
  }, [isRunning, onExecutionStatusChange]);

  // Get output data when execution is complete
  const shouldFetchOutput =
    execution?.status === 'completed' || execution?.status === 'failed';
  const { data: outputData } = usePipelineExecutionOutput(
    execution?.pipelineExecutionId || null,
    {
      enabled: shouldFetchOutput,
    },
  );

  // Fetch recent executions for history dropdown
  const { data: recentExecutionsData, isLoading: isLoadingRecentExecutions } =
    usePipelineExecutionsByPipelineId(pipelineId, {
      enabled: true,
    });

  // Derive right column state from execution state
  const rightColumnState = useMemo((): RightColumnState => {
    if (executionId && execution) {
      if (isRunning) return 'running';
      if (isComplete) return 'complete';
    }
    return manualRightColumnState;
  }, [executionId, execution, isRunning, isComplete, manualRightColumnState]);

  // Derive output JSON from available data sources
  const outputJson = useMemo((): string => {
    if (!s3Content && !outputData) return '';

    let dataToProcess: Record<string, unknown> | null = null;

    if (s3Content) {
      try {
        dataToProcess = JSON.parse(s3Content);
      } catch {
        dataToProcess = { value: s3Content };
      }
    } else if (outputData?.type === 'inline') {
      dataToProcess = (outputData as { data: Record<string, unknown> }).data;
    } else if (execution?.pipelineOutput) {
      dataToProcess = execution.pipelineOutput;
    }

    if (dataToProcess) {
      try {
        return JSON.stringify(dataToProcess, null, 2);
      } catch {
        return JSON.stringify(
          { error: 'Failed to format output data' },
          null,
          2,
        );
      }
    }

    return '';
  }, [s3Content, outputData, execution?.pipelineOutput]);

  // Transform schema to handle file input blocks and add block names/descriptions
  const transformFileInputSchema = useCallback(
    (schema: Record<string, unknown>): Record<string, unknown> => {
      if (!schema || typeof schema !== 'object') return schema;

      const transformed = { ...schema };

      if (schema.properties) {
        const properties = { ...schema.properties } as Record<
          string,
          Record<string, unknown>
        >;

        Object.keys(properties).forEach((key) => {
          const prop = properties[key];
          if (prop && typeof prop === 'object') {
            // Look up the block in pipelineConfig to get name and description
            if (pipelineConfig && typeof pipelineConfig === 'object') {
              const blocks = pipelineConfig.blocks as Record<
                string,
                { name: string; description: string }
              >;
              if (blocks && blocks[key]) {
                const block = blocks[key];
                // Update the property with block's custom name and description
                properties[key] = {
                  ...prop,
                  title: block.name || prop.title || key,
                  description: block.description || prop.description,
                  'x-block-id': key,
                };
              }
            }

            const propProps = prop.properties;
            if (propProps) {
              properties[key] = transformFileInputSchema(
                properties[key] || prop,
              );
            }
          }
        });

        transformed.properties = properties;
      }

      return transformed;
    },
    [pipelineConfig],
  );

  // Memoize transformed schema to prevent unnecessary re-renders that cause input flashing
  const transformedInputSchema = useMemo(() => {
    return inputSchema ? transformFileInputSchema(inputSchema) : null;
  }, [inputSchema, transformFileInputSchema]);

  // Helper to determine if input form should be readonly
  const isInputFormReadonly =
    rightColumnState === 'running' || viewingHistoricalExecution;
  const inputFormContext = viewingHistoricalExecution
    ? 'historical'
    : rightColumnState === 'running'
      ? 'running'
      : 'editing';

  // Memoize custom components to prevent form field re-creation
  const customComponents = useMemo(
    () => ({
      file: (props: {
        schema: Record<string, unknown>;
        disabled?: boolean;
        readonly?: boolean;
        value?: unknown;
        onChange?: (value: unknown) => void;
      }) => (
        <FileSelectField
          teamId={teamId}
          schema={props.schema}
          disabled={props.disabled || isInputFormReadonly}
          readonly={props.readonly || isInputFormReadonly}
          value={props.value as string | undefined}
          onChange={
            props.onChange as ((value: string | undefined) => void) | undefined
          }
        />
      ),
      dataset: (props: {
        schema: Record<string, unknown>;
        disabled?: boolean;
        readonly?: boolean;
        value?: unknown;
        onChange?: (value: unknown) => void;
      }) => (
        <DatasetSelectField
          teamId={teamId}
          schema={props.schema}
          disabled={props.disabled || isInputFormReadonly}
          readonly={props.readonly || isInputFormReadonly}
          value={props.value as string | undefined}
          onChange={
            props.onChange as ((value: string | undefined) => void) | undefined
          }
        />
      ),
    }),
    [teamId, isInputFormReadonly],
  );

  // Handle viewing historical execution
  const handleViewHistoricalExecution = useCallback(
    (execution: NonNullable<typeof recentExecutionsData>['items'][0]) => {
      // Switch to viewing this historical execution
      setExecutionId(execution.pipelineExecutionId);
      setViewingHistoricalExecution(true);

      // Set appropriate right column state based on execution status
      if (execution.status === 'running') {
        setManualRightColumnState('running');
      } else {
        setManualRightColumnState('complete');
      }

      void message.info(
        `Viewing execution: ${execution.pipelineInputSummary || 'Untitled Run'}`,
      );
    },
    [message],
  );

  // Handle view all history action
  const handleViewAllHistory = useCallback(() => {
    setShowHistoryModal(true);
  }, []);

  // Handle S3 content fetching
  useEffect(() => {
    const fetchS3Content = async () => {
      if (
        !outputData ||
        outputData.type !== 's3' ||
        isLoadingS3Content ||
        s3Content
      )
        return;

      setIsLoadingS3Content(true);
      try {
        const response = await fetch(outputData.downloadUrl);
        let content: string;

        if (outputData.metadata.compressionType === 'gzip') {
          const arrayBuffer = await response.arrayBuffer();
          content = await new Response(
            new Response(arrayBuffer).body?.pipeThrough(
              new DecompressionStream('gzip'),
            ),
          ).text();
        } else {
          content = await response.text();
        }

        setS3Content(content);
      } catch (error) {
        // Log error for debugging
        void message.error('Failed to fetch output content');
      } finally {
        setIsLoadingS3Content(false);
      }
    };

    if (outputData?.type === 's3') {
      void fetchS3Content();
    }
  }, [outputData, isLoadingS3Content, s3Content, message]);

  const handleSubmitInputs = async () => {
    if (!configVersionId) {
      void message.error('No configuration version selected');
      return;
    }

    try {
      setIsSubmitting(true);
      const values = await inputForm.validateFields();

      const executionResponse =
        await createPipelineExecutionMutation.mutateAsync({
          pipelineConfigurationVersionId: configVersionId,
          pipelineInput: values,
          pipelineInputSummary: 'Run Modal Execution',
        });

      setExecutionId(executionResponse.pipelineExecutionId);
      setManualRightColumnState('running');
      void message.success('Pipeline execution started');
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to start pipeline execution',
      );
      void message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRunAgain = () => {
    setManualRightColumnState('empty');
    setExecutionId(null);
    setS3Content(null);
    setViewingHistoricalExecution(false);
    // Keep input values for reuse
  };

  const handleBackToNewExecution = () => {
    setManualRightColumnState('empty');
    setExecutionId(null);
    setS3Content(null);
    setViewingHistoricalExecution(false);
    inputForm.resetFields();
  };

  const handleDownloadOutput = () => {
    if (!outputJson) {
      void message.warning('No output data to download');
      return;
    }

    try {
      // Create a blob with the JSON data
      const blob = new Blob([outputJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      // Create a temporary download link
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const executionIdSuffix = execution?.pipelineExecutionId
        ? `_${execution.pipelineExecutionId}`
        : '';
      link.download = `pipeline-output_${timestamp}${executionIdSuffix}.json`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      void message.success('Output data downloaded successfully');
    } catch (error) {
      void message.error('Failed to download output data');
    }
  };

  // const handleCancel = () => {
  //   // TODO: Implement execution cancellation
  //   void message.info('Execution cancellation not yet implemented');
  // };

  // Left column component - Input form
  const renderLeftColumn = () => {
    return (
      <InputPanel>
        <InputPanelHeader>
          <InputTitle>Pipeline Inputs</InputTitle>
          <div style={{ display: 'flex', gap: '8px' }}>
            <RecentRunsDropdown
              executions={recentExecutionsData?.items}
              isLoading={isLoadingRecentExecutions}
              onViewExecution={handleViewHistoricalExecution}
              onViewAll={handleViewAllHistory}
            />
            {rightColumnState === 'empty' && (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleSubmitInputs}
                loading={isSubmitting}
                size="small"
              >
                Run Pipeline
              </Button>
            )}
            {rightColumnState === 'complete' &&
              (viewingHistoricalExecution ? (
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleBackToNewExecution}
                  size="small"
                >
                  New Execution
                </Button>
              ) : (
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleRunAgain}
                  size="small"
                >
                  Run Again
                </Button>
              ))}
          </div>
        </InputPanelHeader>

        {inputFormContext === 'running' && (
          <NotificationBanner type="info">
            These inputs are locked during execution
          </NotificationBanner>
        )}

        {inputFormContext === 'historical' && (
          <NotificationBanner type="warning">
            Viewing historical execution inputs
          </NotificationBanner>
        )}

        <InputFormSection>
          {isValidating ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100px',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <LoadingOutlined style={{ fontSize: '24px' }} />
              <span style={{ color: '#666' }}>
                Validating pipeline configuration...
              </span>
            </div>
          ) : transformedInputSchema ? (
            <InputForm
              form={inputForm}
              layout="vertical"
              name="pipelineInputForm"
              style={{ opacity: isInputFormReadonly ? 0.7 : 1 }}
            >
              <JsonSchemaForm
                schema={transformedInputSchema}
                form={inputForm}
                layout="vertical"
                showDescription
                disabled={isInputFormReadonly}
                customComponents={customComponents}
              />
            </InputForm>
          ) : (
            <Empty description="No input parameters required" />
          )}
        </InputFormSection>
      </InputPanel>
    );
  };

  // Right column component - Progress, output, or empty state
  const renderRightColumn = () => {
    switch (rightColumnState) {
      case 'empty':
        return (
          <EmptyStatePanel>
            <EmptyStateTitle>Ready to Run</EmptyStateTitle>
            <EmptyStateDescription>
              Configure your inputs and click "Run Pipeline" to start.
            </EmptyStateDescription>
          </EmptyStatePanel>
        );

      case 'running':
        return (
          <ProgressPanel>
            <ExecutionHeader>
              <ExecutionTitle>Execution in Progress</ExecutionTitle>
              {execution && (
                <ExecutionMeta>
                  <div>
                    Started: {new Date(execution.createdAt).toLocaleString()}
                  </div>
                  <div>Execution ID: #{execution.pipelineExecutionId}</div>
                </ExecutionMeta>
              )}
            </ExecutionHeader>

            {/* Show overall progress only */}
            <div style={{ textAlign: 'center' }}>
              <AnimatedProgress
                isRunning={isRunning}
                isComplete={isComplete}
                actualProgress={progressData?.overallProgress}
                size={48}
              />
            </div>

            {execution?.status === 'failed' && (
              <ErrorBanner>
                <strong>Error:</strong> Pipeline execution failed
              </ErrorBanner>
            )}
          </ProgressPanel>
        );

      case 'complete':
        return (
          <OutputPanel>
            <OutputPanelHeader>
              <OutputHeader>Output Data</OutputHeader>
              {outputJson && (
                <Button
                  type="default"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadOutput}
                  title="Download output as JSON"
                  size="small"
                >
                  Download (JSON)
                </Button>
              )}
            </OutputPanelHeader>

            <OutputFormSection>
              {outputJson ? (
                <OutputContainer>
                  <JsonEditor
                    value={outputJson}
                    readOnly={true}
                    height="100%"
                    theme={isDarkMode ? 'dark' : 'light'}
                    placeholder="No output data available"
                    lineWrapping={true}
                  />
                </OutputContainer>
              ) : (
                <Empty description="No output data available" />
              )}
            </OutputFormSection>
          </OutputPanel>
        );
    }
  };

  return (
    <>
      <TwoColumnLayout>
        <LeftColumn>{renderLeftColumn()}</LeftColumn>
        <RightColumn>{renderRightColumn()}</RightColumn>
      </TwoColumnLayout>

      {/* Execution History Modal */}
      <ExecutionHistoryModal
        open={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        executions={recentExecutionsData?.items}
        isLoading={isLoadingRecentExecutions}
        onViewOutput={(execution) => {
          handleViewHistoricalExecution(execution);
          setShowHistoryModal(false);
        }}
      />
    </>
  );
};
